/* eslint-disable */
// ---------------------------------------------------------------------------
// Traduction « article de catalogue » → « plat de la carte ».
//
// Le menu ne dispose pas d'un endpoint à lui : il consomme le catalogue Scan & Go
// renvoyé par /verifyaccess, dont les articles ont cette forme (cf. exposerArticle
// côté backend, puis normArticle dans hsigscangoIndexdb.js) :
//
//   { ean, libelle, prix, pu, pu1..pu6, pu7, pv, unite,
//     accompagnement, categorieplat, actif }
//
// `accompagnement` et `categorieplat` viennent de dbarticles.datajsonrestaurant :
// ce sont les deux seuls champs réellement « restaurant » que porte la base.
// `categorieplat` ∈ { PLAT, BOISSON, DESSERT } - cf. le <select> de la fiche
// article (stock/articles/form.vue) : c'est la SECTION de la carte.
//
// Ce qui n'existe NULLE PART en base, et qu'on ne fabrique donc pas :
//   description, image  → lacarte.vue retombe sur la liste des accompagnements
//   platdujour          → volontairement absent : on affiche toute la carte,
//                         sans onglet « Menu du jour » (décision produit)
//   multiaccomp         → absent ⇒ un seul accompagnement par plat
// format.js a un repli pour chacun ; ne pas les inventer ici avec des valeurs
// arbitraires, ce serait mentir à la vue.
// ---------------------------------------------------------------------------

/**
 * `categorieplat` est un TYPE DE SECTION, pas une famille de produits : la fiche
 * article (stock/articles/form.vue) n'offre que ces trois valeurs. C'est donc le
 * découpage naturel de la carte.
 *
 * L'ordre compte - on ne présente pas les desserts avant les plats - et il n'est
 * pas alphabétique. `categories` (menu/index.vue) construit ses sections dans
 * l'ordre de `items` : c'est le tri de catalogueVersCarte() qui le fixe.
 */
const SECTIONS = {
    PLAT:    { rang: 1, libelle: "PLATS" },
    BOISSON: { rang: 2, libelle: "BOISSONS" },
    DESSERT: { rang: 3, libelle: "DESSERTS" },
};

// Le backend garantit 'PLAT' par défaut (IFNULL SQL + `|| "PLAT"` dans
// exposerArticle) ; ce repli ne sert qu'à une valeur historique inattendue.
const SECTION_DEFAUT = { rang: 9, libelle: "AUTRES" };

function section(a) {
    const code = String((a && a.categorieplat) || "").trim().toUpperCase();
    return SECTIONS[code] || SECTION_DEFAUT;
}

/**
 * Les tarifs (= portions) d'un plat.
 *
 * Règle reprise TELLE QUELLE de la caisse restaurant
 * (caisse/facturation/2/formcaisse2.vue, selectionitemforfacturation) :
 *
 *     const pu = [[prixpromo],[pu],[pu1],[pu2],[pu3],[pu4],[pu5],[pu6],[pu7]];
 *     pu.forEach(([v]) => { if (v !== 0) { $cp++; $dp.push(v); } });
 *
 * Autrement dit : TOUT montant non nul de la fiche article est un prix
 * vendable, et le caissier choisit dans cette liste. Le client doit voir
 * exactement les mêmes choix, sans quoi la carte et le ticket divergent.
 *
 * Deux pièges qu'il a fallu corriger :
 *
 *  - `pu7` est un prix comme les autres pour la caisse. L'oublier n'était pas
 *    un détail : sur le magasin de test, pu1 et pu2 sont à zéro partout tandis
 *    que pu7 est renseigné sur 1590 articles sur 2150. Sans lui, presque
 *    aucun plat n'affichait son second tarif.
 *
 *  - la caisse part de `pu` brut, jamais du prix calculé par paliers `pv`
 *    (elle ne consulte pas `pv` du tout). On s'aligne, avec repli sur `prix`
 *    si `pu` est absent, pour ne jamais afficher un plat à 0 F.
 *
 * `prixpromo` n'est pas repris : /verifyaccess ne l'expose pas, et l'ajouter
 * supposerait de toucher au backend.
 *
 * Les montants n'ont aucun libellé en base - d'où « Portion de 2 500 FCFA »,
 * qui nomme la portion par son prix plutôt que par un « Tarif 2 » muet.
 */
export function tarifsDepuisArticle(a) {
    const montants = [];
    const vus = new Set();

    const ajouter = (v) => {
        const n = Number(v) || 0;
        // Le doublon est fréquent (pu7 recopie souvent pu) et afficherait
        // deux boutons identiques dans la fiche plat.
        if (n <= 0 || vus.has(n)) return;
        vus.add(n);
        montants.push(n);
    };

    ajouter(a.pu);
    for (let i = 1; i <= 7; i++) ajouter(a["pu" + i]);
    if (!montants.length) ajouter(a.prix);   // repli : jamais de plat à 0 F

    return montants.map((montant, i) => ({
        pid: String(a.ean) + "-p" + i,
        libelle: "Portion de " + montant.toLocaleString("fr-FR") + " FCFA",
        pu: montant,
        prixpromo: 0,
        defaut: i === 0,
    }));
}

/**
 * Normalise les accompagnements vers { pid, name, supplement }.
 *
 * Le modèle de la caisse (modal_accompagnement.vue) tient en deux règles :
 *
 *  - UN SEUL accompagnement par ligne. La modale émet un objet, pas un
 *    tableau, et la clé de dédoublonnage de la caisse est
 *    `pid--prix--PRIX--accompagnement--ACCPID`. D'où multiaccomp = 0 côté
 *    menu : cumuler des accompagnements produirait une commande que la
 *    caisse ne sait pas représenter.
 *
 *  - AUCUN SUPPLÉMENT. Le référentiel (dbtbfichiers, typetbfichier
 *    'accompagnement') ne stocke qu'un intitulé - « Riz », « Amiwo »,
 *    « Atieke », « Aloco »… - sans prix, et la fiche article n'en enregistre
 *    que { pid, name }. La caisse ne les additionne donc jamais au total, elle
 *    s'en sert comme libellé (`autredesignation`). `supplement` reste ici à 0
 *    pour rester compatible avec ficheplat.vue, qui l'affiche seulement s'il
 *    est > 0 : en pratique le supplément n'apparaît jamais. Ne pas se mettre
 *    à le facturer sans que la caisse en fasse autant.
 *
 * La colonne JSON n'a pas de forme garantie : selon la saisie on y trouve des
 * chaînes nues (["Riz", "Aloco"]) ou des objets. Les deux sont acceptés.
 *
 * ⚠️ ficheplat.vue identifie un accompagnement par son `name` (estChoisi,
 * basculer) et non par son pid. Un nom vide ou dupliqué ferait donc
 * sélectionner deux chips d'un coup : on filtre et on dédoublonne ici.
 */
export function normaliserAccompagnements(brut) {
    if (!Array.isArray(brut)) return [];

    const vus = new Set();
    const sortie = [];

    for (let i = 0; i < brut.length; i++) {
        const item = brut[i];
        const name = String(
            (item && typeof item === "object") ? (item.name || item.libelle || "") : (item || "")
        ).trim();

        if (!name) continue;

        const cle = name.toLowerCase();
        if (vus.has(cle)) continue;
        vus.add(cle);

        sortie.push({
            pid: (item && typeof item === "object" && item.pid)
                ? String(item.pid)
                : String(a_slug(name)) + "-" + i,
            name,
            supplement: (item && typeof item === "object") ? (Number(item.supplement) || 0) : 0,
        });
    }

    return sortie;
}

function a_slug(s) {
    return String(s).normalize("NFD").replace(/[̀-ͯ]/g, "")
        .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

/**
 * Un article du catalogue IndexedDB → un plat exploitable par lacarte.vue.
 *
 * `pid` vaut l'EAN : le backend ne SELECT pas dbarticles.pid, l'EAN est la seule
 * clé stable dont on dispose (c'est déjà la clé primaire du store sgArticles).
 * Il sert de clé de v-for et de clé du panier - il doit rester unique.
 */
export function articleVersPlat(a) {
    return {
        pid: String(a.ean || ""),
        libelle: a.libelle || "",
        catalogue: section(a).libelle,
        unite: a.unite || "",

        // Le catalogue ne transporte pas la quantité en stock : le serveur
        // n'expose que `actif`. `disponible()` (format.js) teste parseFloat > 0,
        // d'où "1"/"0" plutôt qu'un booléen.
        rstck: a.actif === false ? "0" : "1",

        tarifs: tarifsDepuisArticle(a),
        accompagnement: normaliserAccompagnements(a.accompagnement),

        // Chemin de la photo (catalogue au schéma 3), "" s'il n'y en a pas.
        photo: a.photo || "",
    };
}

/**
 * Le catalogue entier → la carte.
 *
 * Aucun filtre : TOUT le catalogue du magasin est servi. On aurait pu croire que
 * `categorieplat` distingue un plat d'un article de supermarché, mais non - le
 * backend force 'PLAT' par défaut, en SQL comme en JS, si bien que chaque
 * article en porte un. Le filtre supposé n'aurait donc rien filtré.
 *
 * Ce n'est pas gênant : un magasin est soit un restaurant, soit un supermarché
 * (le mode est un réglage de la page QR), et le QR menu n'est affiché que dans
 * le premier cas. Son catalogue EST sa carte.
 *
 * Tri : par section d'abord (plats, boissons, desserts), puis alphabétique.
 */
export function catalogueVersCarte(articles) {
    if (!Array.isArray(articles)) return [];
    return articles
        .filter(Boolean)
        .map((a) => ({ plat: articleVersPlat(a), rang: section(a).rang }))
        .sort((x, y) => (x.rang - y.rang) || x.plat.libelle.localeCompare(y.plat.libelle, "fr"))
        .map((e) => e.plat);
}
